import styled, { keyframes } from 'styled-components';

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  background: #f8fafc;
  overflow-y: auto;
  font-family: 'Inter', sans-serif;
`;

export const Header = styled.div`
  margin-bottom: 24px;
  padding-bottom: 10px;
  padding-left: 16px;
  padding-top: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background-color: rgba(13, 148, 136, 0.08);
  border-radius: 8px;
  box-shadow: 0 15px 25px -10px rgba(0, 0, 0, 0.03),
    0 5px 10px -5px rgba(0, 0, 0, 0.02), 0 -4px 10px rgba(0, 0, 0, 0.02);
  animation: ${fadeInUp} 0.4s ease-out;

  h1 {
    font-size: 1.5rem;
    color: #0f172a;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.02em;
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      width: 26px;
      height: 26px;
      opacity: 0.9;
      color: #0f172a;
    }
  }

  p {
    color: #64748b;
    font-size: 0.95rem;
    margin-top: 4px;
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 32px;
`;

export const KpiCard = styled.div<{ $delay: number; $color: string }>`
  background: white;
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: ${({ $delay }) => $delay}s;
  opacity: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1),
      0 8px 10px -6px rgba(0, 0, 0, 0.1);
  }

  .icon-wrapper {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    background: ${({ $color }) => $color}15;
    color: ${({ $color }) => $color};
    flex-shrink: 0;
  }

  .title {
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
    line-height: 1.2;
    white-space: nowrap;
  }

  .value {
    font-size: 1.1rem;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .card-content {
    display: flex;
    flex-direction: column;
    margin-right: 12px;
  }
`;

export const LoadingOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 200px);
  width: 100%;
`;

export const ChartContainerWrapper = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: 0.6s;
  opacity: 0;

  h3 {
    margin: 0 0 20px 0;
    color: #0f172a;
    font-size: 1.15rem;
    font-weight: 700;
  }

  .recharts-wrapper *:focus {
    outline: none !important;
  }
`;

export const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Span2Chart = styled(ChartContainerWrapper)`
  grid-column: span 2;
  margin: 0;
  height: 100%;

  @media (max-width: 1200px) {
    grid-column: span 2;
  }

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

export const Span1Chart = styled(ChartContainerWrapper)`
  grid-column: span 1;
  margin: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const TableWrapper = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-top: 24px;
  overflow-x: auto;
  min-height: fit-content;
  max-height: 750px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  animation: ${fadeInUp} 0.5s ease-out forwards;
  animation-delay: 0.8s;
  opacity: 0;

  h3 {
    margin: 0 0 24px 0;
    color: #0f172a;
    font-size: 1.25rem;
    font-weight: 700;
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 800px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;

  th {
    text-align: center;
    padding: 16px 8px;
    background: #f8fafc;
    color: #64748b;
    font-weight: 600;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #e2e8f0;
  }

  td {
    text-align: center;
    padding: 12px 8px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
    font-size: 0.9rem;
  }

  tbody tr {
    transition: background 0.2s;

    &:nth-child(odd) {
      background: #f0f9ff;
    }

    &:nth-child(even) {
      background: #f8fafc;
    }

    &:hover {
      background: #e2e8f0;
    }
  }
`;

export const Badge = styled.span<{ $color: string; $hoverable?: boolean }>`
  background: ${({ $color }) => $color}15;
  color: ${({ $color }) => $color};
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: ${({ $color, $hoverable }) =>
      $hoverable ? `${$color}40` : `${$color}15`};
  }
`;

export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  margin-top: 16px;

  span {
    font-size: 0.9rem;
    color: #64748b;
  }
`;

export const PageButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #cbd5e1;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DischargeButton = styled.button`
  width: 100%;
  padding: 8px;
  background: #34d399;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;

  &:hover {
    background: #10b981;
  }
`;

export const ChartFootnote = styled.div`
  margin-top: 16px;
  padding-left: 8px;
  font-size: 0.75rem;
  font-style: italic;
  color: #64748b;
`;

export const EmptyRankingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #94a3b8;
  font-size: 0.9rem;
  text-align: center;
`;
